<script lang="ts" setup>
interface AnimatedBeamProps {
  containerRef: HTMLElement
  curvature?: number
  delay?: number
  duration?: number
  endXOffset?: number
  endYOffset?: number
  fromRef: HTMLElement
  gradientStartColor?: string
  gradientStopColor?: string
  pathColor?: string
  pathOpacity?: number
  pathWidth?: number
  reverse?: boolean
  startXOffset?: number
  startYOffset?: number
  toRef: HTMLElement
}

const props = withDefaults(defineProps<AnimatedBeamProps>(), {
  curvature: 0,
  delay: 0,
  duration: 3,
  endXOffset: 0,
  endYOffset: 0,
  gradientStartColor: 'rgba(165, 180, 252, 0.35)',
  gradientStopColor: 'rgba(67, 56, 202, 0.35)',
  pathColor: 'gray',
  pathOpacity: 0.2,
  pathWidth: 2,
  reverse: false,
  startXOffset: 0,
  startYOffset: 0,
})

const id = `beam-${Math.random().toString(36).substring(2, 10)}`
const isVertical = ref(false)
const isRightToLeft = ref(false)
const isBottomToTop = ref(false)
const x1 = computed(() => {
  const direction = props.reverse ? !isRightToLeft.value : isRightToLeft.value

  return direction ? '90%; -10%;' : '10%; 110%;'
})
const x2 = computed(() => {
  const direction = props.reverse ? !isRightToLeft.value : isRightToLeft.value

  return direction ? '100%; 0%;' : '0%; 100%;'
})
const y1 = computed(() => {
  const direction = props.reverse ? !isBottomToTop.value : isBottomToTop.value

  return direction ? '90%; -10%;' : '10%; 110%;'
})
const y2 = computed(() => {
  const direction = props.reverse ? !isBottomToTop.value : isBottomToTop.value

  return direction ? '100%; 0%;' : '0%; 100%;'
})

const pathD = ref('')
const svgDimensions = ref<{ height: number, width: number }>({
  height: 0,
  width: 0,
})

let resizeObserver: ResizeObserver | undefined

watchEffect(() => {
  if (!resizeObserver && props.containerRef) {
    resizeObserver = new ResizeObserver(() => {
      updatePath()
    })
    resizeObserver.observe(props.containerRef)
  }
})

// Function to update the path based on the positions of the elements
function updatePath() {
  if (props.containerRef && props.fromRef && props.toRef) {
    const containerRect = props.containerRef.getBoundingClientRect()
    const rectA = props.fromRef.getBoundingClientRect()
    const rectB = props.toRef.getBoundingClientRect()

    const svgWidth = containerRect.width
    const svgHeight = containerRect.height

    svgDimensions.value = {
      height: svgHeight,
      width: svgWidth,
    }

    const startX = rectA.left - containerRect.left + rectA.width / 2 + (props.startXOffset ?? 0)
    const startY = rectA.top - containerRect.top + rectA.height / 2 + (props.startYOffset ?? 0)
    const endX = rectB.left - containerRect.left + rectB.width / 2 + (props.endXOffset ?? 0)
    const endY = rectB.top - containerRect.top + rectB.height / 2 + (props.endYOffset ?? 0)

    // Check if the light beam is in a vertical direction (the distance in the y-direction is greater than the distance in the x-direction).
    isVertical.value = Math.abs(endY - startY) > Math.abs(endX - startX)

    // Determine the animation direction based on the position relationship between the starting point and the endpoint
    isRightToLeft.value = endX < startX
    isBottomToTop.value = endY < startY

    const controlY = startY - (props.curvature ?? 0)
    const dPath = `M ${startX},${startY} Q ${(startX + endX) / 2},${controlY} ${endX},${endY}`

    pathD.value = dPath
  }
}

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})
</script>

<template>
  <svg
    class="pointer-events-none absolute left-0 top-0 transform-gpu stroke-2"
    fill="none"
    :height="`${svgDimensions.height}`"
    :viewBox="`0 0 ${svgDimensions.width} ${svgDimensions.height}`"
    :width="svgDimensions.width"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      :d="pathD"
      :stroke="pathColor"
      stroke-linecap="round"
      :stroke-opacity="pathOpacity"
      :stroke-width="pathWidth"
    />

    <path
      :d="pathD"
      :stroke="`url(#${id})`"
      stroke-linecap="round"
      stroke-opacity="1"
      :stroke-width="pathWidth"
    />

    <defs>
      <linearGradient
        :id="id"
        gradientUnits="userSpaceOnUse"
        x1="0%"
        x2="0%"
        y1="0%"
        y2="0%"
      >
        <stop
          :stop-color="gradientStartColor"
          stop-opacity="0"
        />

        <stop :stop-color="gradientStartColor" />

        <stop
          offset="32.5%"
          :stop-color="gradientStopColor"
        />

        <stop
          offset="100%"
          :stop-color="gradientStopColor"
          stop-opacity="0"
        />

        <animate
          v-if="!isVertical"
          attributeName="x1"
          :begin="`-${delay}s`"
          calcMode="spline"
          :dur="`${duration}s`"
          keySplines="0.16 0.96 0.64 1"
          keyTimes="0; 1"
          repeatCount="indefinite"
          :values="x1"
        />

        <animate
          v-if="!isVertical"
          attributeName="x2"
          :begin="`-${delay}s`"
          calcMode="spline"
          :dur="`${duration}s`"
          keySplines="0.16 0.96 0.64 1"
          keyTimes="0; 1"
          repeatCount="indefinite"
          :values="x2"
        />

        <animate
          v-if="isVertical"
          attributeName="y1"
          :begin="`-${delay}s`"
          calcMode="spline"
          :dur="`${duration}s`"
          keySplines="0.16 0.96 0.64 1"
          keyTimes="0; 1"
          repeatCount="indefinite"
          :values="y1"
        />

        <animate
          v-if="isVertical"
          attributeName="y2"
          :begin="`-${delay}s`"
          calcMode="spline"
          :dur="`${duration}s`"
          keySplines="0.16 0.96 0.64 1"
          keyTimes="0; 1"
          repeatCount="indefinite"
          :values="y2"
        />
      </linearGradient>
    </defs>
  </svg>
</template>
